import useSWR from "swr";
import useSWRInfinite from "swr/infinite";
import Cookies from "js-cookie";
import { useTranslation } from "react-i18next";
import { useMemo, useCallback } from "react";
import API from "../../services/apiHelpers";

// ==================== POLLS ====================

/**
 * Hook to fetch a list of polls with filtering and pagination
 * @param {Object} options
 * @param {string} options.langCode - Language code (PL, GB, DE, FR, etc.)
 * @param {string} options.groupingId - UUID grouping polls in different languages
 * @param {number} options.publishStatus - 1 (DRAFT), 2 (PENDING), 3 (APPROVED) - admin only
 * @param {string} options.search - Search in title, description, content
 * @param {string} options.ordering - Sort: created_at, -created_at, published_at, title
 * @param {number} options.limit - Items per page
 * @param {number} options.offset - Pagination offset
 */
export function usePolls({
  langCode,
  groupingId,
  publishStatus,
  search,
  ordering = "-published_at",
  limit = 10,
  offset = 0,
} = {}) {
  const { i18n } = useTranslation();
  const url = "/api/v1/polls-v2/";

  const { data, error, isValidating, mutate } = useSWR(
    [url, langCode, groupingId, publishStatus, search, ordering, limit, offset],
    () => {
      const token = Cookies.get("user_token");

      const params = {
        lang_code:
          langCode ||
          (Cookies.get("user_lang")
            ? Cookies.get("user_lang").toUpperCase()
            : i18n.language.toUpperCase()),
        limit,
        offset,
        ordering,
      };

      if (groupingId) params.grouping_id = groupingId;
      if (publishStatus) params.publish_status = publishStatus;
      if (search) params.search = search;

      const headers = token
        ? {
            Authorization: `Bearer ${token}`,
          }
        : {};

      return API().get(url, {
        params,
        headers,
      });
    }
  );

  return {
    polls: data && data.data && data.data.results ? data.data.results : [],
    count: data && data.data && data.data.count ? data.data.count : 0,
    next: data && data.data ? data.data.next : null,
    previous: data && data.data ? data.data.previous : null,
    isLoading: isValidating,
    isError: error,
    mutate,
  };
}

/**
 * Hook to fetch polls with infinite scroll/pagination
 * @param {Object} options
 * @param {string} options.langCode - Language code
 * @param {string} options.groupingId - UUID grouping
 * @param {number} options.publishStatus - Publish status filter
 * @param {string} options.search - Search query
 * @param {string} options.ordering - Sort order
 * @param {number} options.limit - Items per page (default: 10)
 */
export function usePollsInfinite({
  langCode,
  groupingId,
  publishStatus,
  search,
  ordering = "-published_at",
  limit = 10,
} = {}) {
  const { i18n } = useTranslation();
  const url = "/api/v1/polls-v2/";

  const {
    data,
    mutate,
    size,
    setSize,
    isValidating,
    isLoading,
    error,
  } = useSWRInfinite(
    pageIndex => {
      const offset = limit * pageIndex;
      return JSON.stringify({
        url,
        langCode,
        groupingId,
        publishStatus,
        search,
        ordering,
        offset,
        limit,
        lang: i18n.language,
      });
    },
    req => {
      const {
        url,
        langCode,
        groupingId,
        publishStatus,
        search,
        ordering,
        limit,
        offset,
        lang,
      } = JSON.parse(req);
      const token = Cookies.get("user_token");

      const params = {
        lang_code:
          langCode ||
          (Cookies.get("user_lang")
            ? Cookies.get("user_lang").toUpperCase()
            : lang.toUpperCase()),
        limit,
        offset,
        ordering,
      };

      if (groupingId) params.grouping_id = groupingId;
      if (publishStatus) params.publish_status = publishStatus;
      if (search) params.search = search;

      const headers = token
        ? {
            Authorization: `Bearer ${token}`,
          }
        : {};

      return API().get(url, {
        params,
        headers,
      });
    },
    { revalidateFirstPage: false }
  );

  const pollsData = data ? data.flatMap(({ data }) => data.results) : [];

  const nextPageAvailable = useMemo(() => {
    if (!data || data.length === 0) return false;
    const lastPage = data[data.length - 1];
    return !!(lastPage && lastPage.data && lastPage.data.next);
  }, [data]);

  return {
    polls: pollsData,
    isLoading,
    isValidating,
    isError: error,
    mutate,
    page: size,
    nextPage: () => setSize(size + 1),
    nextPageAvailable,
  };
}

/**
 * Hook to fetch a single poll by slug
 * @param {string} slug - Poll slug (unique identifier)
 */
export function usePoll(slug) {
  const url = slug ? `/api/v1/polls-v2/${slug}/` : null;

  const { data, error, isValidating, mutate } = useSWR(url, () => {
    const token = Cookies.get("user_token");

    const headers = token
      ? {
          Authorization: `Bearer ${token}`,
        }
      : {};

    return API().get(url, { headers });
  });

  return {
    poll: data && data.data ? data.data : null,
    isLoading: isValidating,
    isError: error,
    mutate,
  };
}

/**
 * Hook to get the promoted popup poll for a language
 * @param {string} langCode - Language code (required)
 */
export function usePromotedPopupPoll(langCode) {
  const { i18n } = useTranslation();
  const url = "/api/v1/polls-v2/promoted_popup/";

  const { data, error, isValidating, mutate } = useSWR(
    langCode ? [url, langCode] : null,
    () => {
      const token = Cookies.get("user_token");

      const params = {
        lang_code:
          langCode ||
          (Cookies.get("user_lang")
            ? Cookies.get("user_lang").toUpperCase()
            : i18n.language.toUpperCase()),
      };

      const headers = token
        ? {
            Authorization: `Bearer ${token}`,
          }
        : {};

      return API().get(url, {
        params,
        headers,
      });
    }
  );

  return {
    poll: data && data.data ? data.data : null,
    isLoading: isValidating,
    isError: error,
    mutate,
  };
}

/**
 * Hook to get the promoted bottom poll for a language
 * @param {string} langCode - Language code (required)
 */
export function usePromotedBottomPoll(langCode) {
  const { i18n } = useTranslation();
  const url = "/api/v1/polls-v2/promoted_bottom/";

  const { data, error, isValidating, mutate } = useSWR(
    langCode ? [url, langCode] : null,
    () => {
      const token = Cookies.get("user_token");

      const params = {
        lang_code:
          langCode ||
          (Cookies.get("user_lang")
            ? Cookies.get("user_lang").toUpperCase()
            : i18n.language.toUpperCase()),
      };

      const headers = token
        ? {
            Authorization: `Bearer ${token}`,
          }
        : {};

      return API().get(url, {
        params,
        headers,
      });
    }
  );

  return {
    poll: data && data.data ? data.data : null,
    isLoading: isValidating,
    isError: error,
    mutate,
  };
}

/**
 * Hook to create a new poll (admin only)
 * Returns a function to create poll
 */
export function useCreatePoll() {
  const createPoll = useCallback(async pollData => {
    const token = Cookies.get("user_token");
    const url = "/api/v1/polls-v2/";

    if (!token) {
      throw new Error("Authentication required");
    }

    const response = await API().post(url, pollData, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });

    return response.data;
  }, []);

  return { createPoll };
}

/**
 * Hook to update a poll (admin only)
 * Returns a function to update poll
 * @returns {Object} Object with updatePoll function
 */
export function useUpdatePoll() {
  const updatePoll = useCallback(async (slug, pollData) => {
    const token = Cookies.get("user_token");
    const url = `/api/v1/polls-v2/${slug}/`;

    if (!token) {
      throw new Error("Authentication required");
    }

    const response = await API().patch(url, pollData, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });

    return response.data;
  }, []);

  return { updatePoll };
}

/**
 * Hook to delete a poll (admin only)
 * Returns a function to delete poll
 * @returns {Object} Object with deletePoll function
 */
export function useDeletePoll() {
  const deletePoll = useCallback(async slug => {
    const token = Cookies.get("user_token");
    const url = `/api/v1/polls-v2/${slug}/`;

    if (!token) {
      throw new Error("Authentication required");
    }

    await API().delete(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return true;
  }, []);

  return { deletePoll };
}

// ==================== POLL POPUPS ====================

/**
 * Hook to fetch poll popups list
 * @param {string} langCode - Filter by language code (optional)
 */
export function usePollPopups({ langCode, poll_id } = {}) {
  const { i18n } = useTranslation();
  const url = "/api/v1/poll-v2-popups/";

  const { data, error, isValidating, mutate } = useSWR(
    [url, langCode, poll_id],
    () => {
      const token = Cookies.get("user_token");

      const params = {
        poll_id: poll_id || null,
      };
      if (langCode) {
        params.lang_code =
          langCode ||
          (Cookies.get("user_lang")
            ? Cookies.get("user_lang").toUpperCase()
            : i18n.language.toUpperCase());
      }

      const headers = token
        ? {
            Authorization: `Bearer ${token}`,
          }
        : {};

      return API().get(url, {
        params,
        headers,
      });
    }
  );

  return {
    popups: data && data.data && data.data.results ? data.data.results : [],
    count: data && data.data && data.data.count ? data.data.count : 0,
    isLoading: isValidating,
    isError: error,
    mutate,
  };
}

/**
 * Hook to fetch a single poll popup by ID
 * @param {number|string} popupId - Popup ID
 */
export function usePollPopup(popupId) {
  const url = popupId ? `/api/v1/poll-v2-popups/${popupId}/` : null;

  const { data, error, isValidating, mutate } = useSWR(url, () => {
    const token = Cookies.get("user_token");

    const headers = token
      ? {
          Authorization: `Bearer ${token}`,
        }
      : {};

    return API().get(url, { headers });
  });

  return {
    popup: data && data.data ? data.data : null,
    isLoading: isValidating,
    isError: error,
    mutate,
  };
}

/**
 * Hook to get the active popup for display on homepage
 * @param {string} langCode - Language code (required)
 */
export function useActivePollPopup(langCode) {
  const { i18n } = useTranslation();
  const url = "/api/v1/poll-v2-popups/active/";

  const { data, error, isValidating, mutate } = useSWR(
    langCode ? [url, langCode] : null,
    () => {
      const token = Cookies.get("user_token");

      const params = {
        lang_code:
          langCode ||
          (Cookies.get("user_lang")
            ? Cookies.get("user_lang").toUpperCase()
            : i18n.language.toUpperCase()),
      };

      const headers = token
        ? {
            Authorization: `Bearer ${token}`,
          }
        : {};

      return API().get(url, {
        params,
        headers,
      });
    }
  );

  return {
    popup: data && data.data ? data.data : null,
    isLoading: isValidating,
    isError: error,
    mutate,
  };
}

/**
 * Hook to create a poll popup (admin only)
 * Returns a function to create popup
 */
export function useCreatePollPopup() {
  const createPopup = useCallback(async popupData => {
    const token = Cookies.get("user_token");
    const url = "/api/v1/poll-v2-popups/";

    if (!token) {
      throw new Error("Authentication required");
    }

    const response = await API().post(url, popupData, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });

    return response.data;
  }, []);

  return { createPopup };
}

/**
 * Hook to update a poll popup (admin only)
 * Returns a function to update popup
 */
export function useUpdatePollPopup() {
  const updatePopup = useCallback(async (popupId, popupData) => {
    const token = Cookies.get("user_token");
    const url = `/api/v1/poll-v2-popups/${popupId}/`;

    if (!token) {
      throw new Error("Authentication required");
    }

    const response = await API().patch(url, popupData, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });

    return response.data;
  }, []);

  return { updatePopup };
}

/**
 * Hook to delete a poll popup (admin only)
 * Returns a function to delete popup
 */
export function useDeletePollPopup() {
  const deletePopup = useCallback(async popupId => {
    const token = Cookies.get("user_token");
    const url = `/api/v1/poll-v2-popups/${popupId}/`;

    if (!token) {
      throw new Error("Authentication required");
    }

    await API().delete(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return true;
  }, []);

  return { deletePopup };
}

// ==================== POLL RESPONSES ====================

/**
 * Hook to fetch poll responses list
 * @param {number} pollId - Filter responses by poll ID (optional)
 */
export function usePollResponses(pollId) {
  const url = "/api/v1/poll-v2-responses/";

  const { data, error, isValidating, mutate } = useSWR([url, pollId], () => {
    const token = Cookies.get("user_token");

    const params = {};
    if (pollId) params.poll_id = pollId;

    const headers = token
      ? {
          Authorization: `Bearer ${token}`,
        }
      : {};

    return API().get(url, {
      params,
      headers,
    });
  });

  return {
    responses: data && data.data && data.data.results ? data.data.results : [],
    count: data && data.data && data.data.count ? data.data.count : 0,
    isLoading: isValidating,
    isError: error,
    mutate,
  };
}

/**
 * Hook to fetch poll responses with infinite scroll
 * @param {number} pollId - Filter responses by poll ID (optional)
 * @param {number} limit - Items per page (default: 20)
 */
export function usePollResponsesInfinite({ pollId, limit = 20 } = {}) {
  const url = "/api/v1/poll-v2-responses/";

  const {
    data,
    mutate,
    size,
    setSize,
    isValidating,
    isLoading,
    error,
  } = useSWRInfinite(
    pageIndex => {
      const offset = limit * pageIndex;
      return JSON.stringify({
        url,
        pollId,
        offset,
        limit,
      });
    },
    req => {
      const { url, pollId, limit, offset } = JSON.parse(req);
      const token = Cookies.get("user_token");

      const params = {
        limit,
        offset,
      };

      if (pollId) params.poll_id = pollId;

      const headers = token
        ? {
            Authorization: `Bearer ${token}`,
          }
        : {};

      return API().get(url, {
        params,
        headers,
      });
    },
    { revalidateFirstPage: false }
  );

  const responsesData = data ? data.flatMap(({ data }) => data.results) : [];

  const nextPageAvailable = useMemo(() => {
    if (!data || data.length === 0) return false;
    const lastPage = data[data.length - 1];
    return !!(lastPage && lastPage.data && lastPage.data.next);
  }, [data]);

  return {
    responses: responsesData,
    isLoading,
    isValidating,
    isError: error,
    mutate,
    page: size,
    nextPage: () => setSize(size + 1),
    nextPageAvailable,
  };
}

/**
 * Hook to fetch a single poll response by ID
 * @param {number|string} responseId - Response ID
 */
export function usePollResponse(responseId) {
  const url = responseId ? `/api/v1/poll-v2-responses/${responseId}/` : null;

  const { data, error, isValidating, mutate } = useSWR(url, () => {
    const token = Cookies.get("user_token");

    const headers = token
      ? {
          Authorization: `Bearer ${token}`,
        }
      : {};

    return API().get(url, { headers });
  });

  return {
    response: data && data.data ? data.data : null,
    isLoading: isValidating,
    isError: error,
    mutate,
  };
}

/**
 * Hook to submit a poll response
 * Available to all users (including anonymous)
 * Returns a function to submit response
 */
export function useSubmitPollResponse() {
  const submitResponse = useCallback(async responseData => {
    const token = Cookies.get("user_token");
    const url = "/api/v1/poll-v2-responses/";

    const headers = {
      "Content-Type": "application/json",
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await API().post(url, responseData, { headers });

    return response.data;
  }, []);

  return { submitResponse };
}

/**
 * Hook to update a poll response (admin only)
 * Returns a function to update response
 */
export function useUpdatePollResponse() {
  const updateResponse = useCallback(async (responseId, responseData) => {
    const token = Cookies.get("user_token");
    const url = `/api/v1/poll-v2-responses/${responseId}/`;

    if (!token) {
      throw new Error("Authentication required");
    }

    const response = await API().patch(url, responseData, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });

    return response.data;
  }, []);

  return { updateResponse };
}

/**
 * Hook to delete a poll response (admin only)
 * Returns a function to delete response
 */
export function useDeletePollResponse() {
  const deleteResponse = useCallback(async responseId => {
    const token = Cookies.get("user_token");
    const url = `/api/v1/poll-v2-responses/${responseId}/`;

    if (!token) {
      throw new Error("Authentication required");
    }

    await API().delete(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return true;
  }, []);

  return { deleteResponse };
}
